import { useMemo } from 'react';
import { ANY, Portals } from '../../../content/portal/portals';
import { usePortalContext } from '../../../contexts/portalContext';
import {
  GetCareRecommendationStatusForUserQuery,
  useGetCareRecommendationStatusForUserQuery,
  useHomePageUserQuery,
  useOrganizationConnectNowQuery,
  useOrganizationHasCoachingQuery,
  useOrganizationSingleStopQuery,
} from '../../../graphQL';
import { RoutableKeys } from '../../../routes';
import { SURVEY_STATUSES, useGetSurveyStatus } from '../../survey/core/hooks/useGetSurveyStatus';

type SurveyData =
  | {
      status: 'Incomplete';
      name: string;
      estimatedCompletionMinutes: string;
    }
  | {
      status: 'Completed';
      name: string;
    };

type UseHomePageReturn =
  | {
      loading: true;
      data: undefined;
    }
  | {
      loading: false;
      data: {
        orgName: string;
        recommendationStatus:
          | GetCareRecommendationStatusForUserQuery['getCareRecommendationStatusForUser']
          | undefined;
        getCareCardRoute: Extract<RoutableKeys, 'therapy' | 'therapyNoSelfRefer'>;
        userGreeting: string;
        show: {
          campusResources: boolean;
          coaching: boolean;
          findCare: boolean;
          myCare: boolean; // Active or onboarding in therapy or psych
          getCare: boolean; // Non-Loca Therapy Gateway access to Therapy page
          survey: {
            enabled: boolean;
            data?: SurveyData;
          };
          skills: boolean; // Skills portal access
          connectNow: boolean;
          studentReferral: boolean;
          otherServices: {
            togetherAll: boolean;
            singleStop: {
              enabled: boolean;
              url: string;
            };
          };
        };
      };
    };

/**
 * Loads most of the information needed to display the home page.
 */
export const useHomePage = (): UseHomePageReturn => {
  const { onlyIfPortalIsActive } = usePortalContext();

  const { data: userData, ...userState } = useHomePageUserQuery();
  const { data: orgHasCoachingData, ...orgCoachingState } = useOrganizationHasCoachingQuery();
  const { data: orgHasConnectNowData, ...orgConnectNowState } = useOrganizationConnectNowQuery();
  const { data: orgSingleStopData, ...orgSingleStopState } = useOrganizationSingleStopQuery();
  const { data: recommendationStatusForUser, ...userCareRecState } =
    useGetCareRecommendationStatusForUserQuery();

  const shouldSkipBeaconSurvey = !onlyIfPortalIsActive(Portals.SaiSurvey, true, false);
  const surveyStatusResult = useGetSurveyStatus(shouldSkipBeaconSurvey);
  const surveyData = useMemo(() => {
    if (shouldSkipBeaconSurvey || surveyStatusResult.loading || !surveyStatusResult.success) {
      return undefined;
    }

    const survey = surveyStatusResult.survey;

    if (survey.status === SURVEY_STATUSES.Incomplete) {
      return {
        status: survey.status,
        name: survey.name,
        estimatedCompletionMinutes: survey.estimatedCompletionMinutes,
      };
    }

    if (survey.status === SURVEY_STATUSES.Completed) {
      return {
        status: survey.status,
        name: survey.name,
      };
    }
    return undefined;
  }, [shouldSkipBeaconSurvey, surveyStatusResult]);

  const isLoading: boolean = useMemo(() => {
    const states = [
      userState,
      orgCoachingState,
      orgConnectNowState,
      orgSingleStopState,
      userCareRecState,
    ];

    const surveyLoading = !shouldSkipBeaconSurvey && surveyStatusResult.loading;
    return surveyLoading || states.some(({ loading }) => loading);
  }, [
    userState,
    orgCoachingState,
    orgConnectNowState,
    orgSingleStopState,
    userCareRecState,
    shouldSkipBeaconSurvey,
    surveyStatusResult,
  ]);

  if (isLoading) {
    return {
      data: undefined,
      loading: true,
    };
  }

  const onboardedHubUser = userData?.onboardedHubUser;
  const recommendationStatus = recommendationStatusForUser?.getCareRecommendationStatusForUser;

  const orgName = onboardedHubUser?.organization?.name ?? '';
  const userName = onboardedHubUser?.preferredName ?? '';
  const userGreeting = userName !== '' ? `, ${userName}` : '';

  const showCampusResources = onboardedHubUser?.organizationCanAccessCampusResources ?? false;
  const showCoaching = orgHasCoachingData?.onboardedHubUser.organizationHasCoaching ?? false;
  const showFindCare = onlyIfPortalIsActive(Portals.Loca, true, false);
  const showConnectNow = orgHasConnectNowData?.organizationConnectNow?.connectNowEnabled ?? false;
  const showStudentReferral = onboardedHubUser?.organizationHasSelfReferrals ?? false;
  const orgHasClinicalSelfReferrals =
    onboardedHubUser?.organizationHasClinicalSelfReferrals ?? false;

  const showMyCare = onlyIfPortalIsActive(ANY(Portals.Therapy, Portals.Psychiatry), true, false);
  const showGetCare = onlyIfPortalIsActive(Portals.NonLocaTherapyGateway, true, false);
  const getCareCardRoute = orgHasClinicalSelfReferrals ? 'therapy' : 'therapyNoSelfRefer';

  const showTogetherAll = onboardedHubUser?.organizationCanAccessTogetherAll ?? false;
  const singleStopUrl = orgSingleStopData?.organizationSingleStop?.singleStopUrl ?? '';
  const showSingleStop = orgSingleStopData?.organizationSingleStop.singleStopEnabled ?? false;

  const showSkills = onlyIfPortalIsActive(Portals.Skills, true, false);

  const showBeaconSurvey = onlyIfPortalIsActive(Portals.SaiSurvey, true, false);

  return {
    data: {
      orgName,
      recommendationStatus,
      getCareCardRoute,
      userGreeting,
      show: {
        campusResources: showCampusResources,
        coaching: showCoaching,
        findCare: showFindCare,
        getCare: showGetCare,
        myCare: showMyCare,
        connectNow: showConnectNow,
        survey: {
          enabled: showBeaconSurvey,
          data: surveyData,
        },
        skills: showSkills,
        studentReferral: showStudentReferral,
        otherServices: {
          togetherAll: showTogetherAll,
          singleStop: {
            enabled: showSingleStop && !!singleStopUrl,
            url: singleStopUrl,
          },
        },
      },
    },
    loading: false,
  };
};
